import { Activity, AlertTriangle, CheckCircle2, Clock3, XCircle } from "lucide-react";
import { useState } from "react";
import { apiDelete, getErrorMessage, post, put, useInfiniteScroll, usePoll } from "@/lib/api";
import { formatUtcTime } from "../lib/operatorFormatters";
import InfiniteListFooter from "./InfiniteListFooter";

const statusIcon = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "filled" || normalized === "closed") return <CheckCircle2 size={13} />;
  if (normalized.includes("pending") || normalized.includes("new")) return <Clock3 size={13} />;
  if (normalized.includes("reject") || normalized.includes("cancel")) return <XCircle size={13} />;
  return <AlertTriangle size={13} />;
};

export default function ExecutionPanel({ orders: rawOrders, listState = {}, pending = 0 }) {
  const orders = Array.isArray(rawOrders) ? rawOrders : (Array.isArray(rawOrders?.data) ? rawOrders.data : []);
  const handleScroll = useInfiniteScroll(listState.loadMore || (() => {}), listState.hasMore);
  const approvalQuery = usePoll("/execution/approval", 15000, { mode: "auto", pending: [] });
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const approval = approvalQuery.data;

  const setMode = async (mode) => {
    setBusy(`mode-${mode}`); setMessage("");
    try {
      await put("/execution/approval-mode", { mode });
      setMessage(
        mode === "manual"
          ? "Human approval mode active"
          : mode === "shadow"
            ? "Shadow mode active — decisions record, no order is sent"
            : "Demo auto mode active"
      );
      approvalQuery.retry();
    } catch (error) {
      setMessage(getErrorMessage(error));
    } finally {
      setBusy("");
    }
  };
  const approve = async (id) => {
    setBusy(`approve-${id}`); setMessage("");
    try {
      const response = await post(`/execution/approvals/${id}/approve`);
      setMessage(response.data.message);
      approvalQuery.retry();
    } catch (error) {
      setMessage(getErrorMessage(error));
      approvalQuery.retry();
    } finally {
      setBusy("");
    }
  };
  const dismiss = async (id) => {
    setBusy(`dismiss-${id}`); setMessage("");
    try {
      await apiDelete(`/execution/approvals/${id}`);
      setMessage("Approval dismissed — no order sent");
      approvalQuery.retry();
    } catch (error) {
      setMessage(getErrorMessage(error));
    } finally {
      setBusy("");
    }
  };

  return (
    <section className="execution-panel" data-testid="execution-panel">
      <div className="execution-heading">
        <div><span className="eyebrow">EXECUTION / TELEMETRY</span><h3>Order lifecycle</h3></div>
        <span className="execution-pending"><Activity size={12} /> {pending} pending</span>
      </div>
      <section className="entry-approval-panel" data-testid="entry-approval-panel">
        <div className="entry-approval-heading">
          <div><span>ENTRY CONTROL</span><b>{approval?.mode === "manual" ? "HUMAN APPROVAL" : approval?.mode === "shadow" ? "SHADOW / NO ORDER" : "DEMO AUTO"}</b></div>
          <small>{approval?.pending?.length || 0} / {approval?.max_pending || 5} queued</small>
        </div>
        <p>Auto mode stays restricted to Demo/Testnet/Paper. Shadow records every valid decision without an order. Human approval rechecks market, risk and reconciliation before submission.</p>
        <div className="entry-approval-actions">
          <button className={approval?.mode === "auto" ? "active" : ""} onClick={() => setMode("auto")} disabled={!!busy}>Demo auto</button>
          <button className={approval?.mode === "manual" ? "active" : ""} onClick={() => setMode("manual")} disabled={!!busy}>Human approval</button>
          <button className={approval?.mode === "shadow" ? "active" : ""} onClick={() => setMode("shadow")} disabled={!!busy}>Shadow</button>
        </div>
        {message && <div className="entry-approval-message">{message}</div>}
        {!!approval?.pending?.length && <div className="entry-approval-list">
          {approval.pending.map((item) => (
            <div className="entry-approval-row" key={item.id}>
              <div><b>{item.symbol?.replace(":USDT", "")}</b> <span className={item.side === "long" ? "text-success" : "text-danger"}>{item.side?.toUpperCase()}</span><small>{Number(item.confidence).toFixed(2)} confidence · {item.approval_reason || "manual review"} · expires {formatUtcTime(item.expires_at, { fallback: "expiry unavailable" })}</small></div>
              <div><button onClick={() => approve(item.id)} disabled={!!busy}>{busy === `approve-${item.id}` ? "Checking…" : "Approve"}</button><button className="dismiss" onClick={() => dismiss(item.id)} disabled={!!busy}>Dismiss</button></div>
            </div>
          ))}
        </div>}
      </section>
      {!orders?.length ? (
        <p className="telemetry-empty">No orders recorded yet.</p>
      ) : (
        <div className="execution-list" onScroll={handleScroll}>
          {orders.map((order) => (
            <div className="execution-row" key={order.id}>
              <span className="execution-symbol">{order.symbol?.replace(":USDT", "")}</span>
              <span className={order.side === "buy" ? "text-success" : "text-danger"}>{order.side?.toUpperCase()}</span>
              <span className="execution-status">{statusIcon(order.status)} {order.status}</span>
              <span className="execution-value">{order.executed_qty || order.qty} @ {order.avg_price || order.price || "—"}</span>
              <span className="execution-fee">{order.fee ? `fee ${Number(order.fee).toFixed(4)}` : "fee —"}</span>
            </div>
          ))}
          <InfiniteListFooter
            loadingMore={listState.loadingMore}
            hasMore={listState.hasMore}
            error={listState.error}
          />
        </div>
      )}
    </section>
  );
}
